// login.ts - WAAS Login API Example

import axios from 'axios';
import jwt, { JwtHeader, JwtPayload } from 'jsonwebtoken';
import qs from 'qs';
import { createSecureChannel, encrypt } from './securechannel';
import queryString from 'query-string';

import { z } from 'zod';
import { isTokenExpired } from '../../utils/checkToken';
import env from '../../env';

const clientID = process.env.REACT_APP_ABC_WAAS_CLIENT_ID!;
const clientSecret = process.env.REACT_APP_ABC_WAAS_CLIENT_SECRET!;

export const AbcLoginResponse = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  token_type: z.string(),
  expire_in: z.number(),
});

export interface AbcLoginResult {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  refreshToken: string;
  code?: number;
  msg?: string;
  timestamp?: number;
}

/*
    This example assumes normal operation and does not handle errors separately.
    Error and exception handling should be applied during implementation.
    Example written to build ts to js and run the dist file by configuring package.json.
    Refer to the script in package.json.
    // ``` json
        "scripts": {
        "start": "tsc | node dist/index.js",
        },
    // ```
*/

const WAAS_BASE_URL = 'https://api.waas.myabcwallet.com';

// Not a mandatory function. Utility function.
function getBaseURL(): string {
  const waas_base_url: string = env.REACT_APP_ABC_WAAS_BASE_URL || WAAS_BASE_URL;
  return waas_base_url;
}

type emailLoginResult = {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiredIn: string;
};

const API_URL = `${env.REACT_APP_API_SERVER}/api/waas`;

export async function emailLogin(
  email: string,
  encryptedPassword: string,
  secureChannelID: string,
  auth: string
): Promise<emailLoginResult> {
  /*
      Sends a login request using email and password.

      Args:
          email (str): The user's email address.
          encrypted_password (str): The encrypted user password. It must be encrypted with a secure channel.
          secure_channel_id (str): The secure channel ID.
          auth (str): Encoded authentication information. It is the base64 encoded value of the issued Client ID and Client Secret.

      Returns:
          EmailLoginResultDict: The server's response to the login request.

      Raises:
          HTTPError: If the login request fails.
  */
  try {
    const urlStr = `${getBaseURL()}/auth/auth-service/v2/login`;
    const data = qs.stringify({
      grant_type: 'password',
      username: email,
      password: encryptedPassword,
      audience: 'https://mw.myabcwallet.com',
    });

    const response = await axios.post(urlStr, data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Secure-Channel': secureChannelID,
        Authorization: `Basic ${auth}`,
      },
    });

    const res: emailLoginResult = {
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
      tokenType: response.data.token_type,
      expiredIn: response.data.expired_in,
    };

    return res;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `fail to email login. stataus code: ${error.response?.status}, data: ${JSON.stringify(error.response?.data)}`
      );
    }

    throw new Error(`fail to email login`);
  }
}

export const tokenLogin = async (token: string, service: string): Promise<AbcLoginResult> => {
  // eslint-disable-next-line no-useless-catch
  try {
    const res = await axios.request({
      url: `${API_URL}/login`,
      method: 'post',
      // adapter: fetchAdapter,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        token,
        service,
        audience: env.ABC_SERVICE_ID,
      }),
    });
    if (res.status !== 200) {
      throw new Error();
    }

    let abcAuth: any;
    if (res.data?.code === 618) {
      abcAuth = {
        code: res.data.code,
        msg: res.data.msg,
        timeout: res.data.timeout,
      };
    } else {
      const resData = AbcLoginResponse.parse(res.data);
      abcAuth = {
        accessToken: resData.access_token,
        refreshToken: resData.refresh_token,
        tokenType: resData.token_type,
        expiresIn: resData.expire_in,
      };
      window.sessionStorage.setItem('abcAuth', JSON.stringify(abcAuth));
    }
    return abcAuth;
  } catch (error: any) {
    // throw error?.response?.data;
    throw error;
  }
};

export async function refreshToken(): Promise<emailLoginResult | undefined> {
  /*
      Reissues an access token using a refresh token.

      Args:
          refresh_token (str): The refresh token.
          auth (str): Encoded authentication information. It is the base64 encoded value of the issued Client ID and Client Secret.

      Returns:
          EmailLoginResultDict: The reissued access token.
  */

  const refreshToken = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}').refreshToken;
  const auth = Buffer.from(`${clientID}:${clientSecret}`).toString('base64');

  try {
    const isExpired = isTokenExpired(refreshToken);

    if (isExpired) {
      alert('refresh token is expired. 다시 로그인 주세요.');
    } else {
      const urlStr = `${getBaseURL()}/auth/auth-service/v2/refresh`;
      const data = qs.stringify({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      });

      const response = await axios.post(urlStr, data, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${auth}`,
        },
      });

      const newToken: emailLoginResult = {
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
        tokenType: response.data.token_type,
        expiredIn: response.data.expired_in,
      };

      window.sessionStorage.setItem('abcAuth', JSON.stringify(newToken));
      return newToken;
    }
  } catch (error: any) {
    console.log('!! refreshToken error : ', error.response?.data);
    return {
      accessToken: '',
      refreshToken: '',
      tokenType: '',
      expiredIn: '',
    };
  }
}

interface JWKKey {
  kid: string;
  [key: string]: any;
}

interface JWKDict {
  keys: JWKKey[];
}

async function verifyToken(token: string): Promise<boolean> {
  /*
      Validates the token.

      1. Extracts the user_pool_id from the token.
      2. Retrieves the JWK list using the user_pool_id.
      3. Finds the JWK that matches the kid value in the token header and validates the token.

      jwks.json example:
      >>> {
          "keys": [
              {
                  "kty": "EC",
                  "use": "sig",
                  "crv": "P-256",
                  "kid": "0",
                  "x": "ZrVThPhiQSQw1YQcuXjD1qm2stKQty2N1L8gnWDVtzU",
                  "y": "B6nqJgdH00TIPJkINiT6JzfDfteKVLYtP0x3NuaCpbY",
                  "alg": "ES256",
              }
          ]
      }

      decoded_token data example:
      >>> {
          "sub": "85abcd789a0749e0b8de39226c05f81c",
          "aud": "https://mw.myabcwallet.com",
          "iss": "https://dev-api.id.myabcwallet.com/266021e24dd0bfaaa96f2b5e21d7c800",
          "pid": "5babdf06-2a5c-4d17-88f5-2998a3db7e21",
          "exp": 1727416466,
          "iat": 1727415866,
          "jti": "bc5c7a5f29684063bdd332b9262d1c7b",
          "user-agent": "python-requests/2.32.3",
      }

      Args:
          token (str): The JWT token to be validated.

      Returns:
          bool: The result of the validation.
  */
  try {
    // Decoding JWT token (without signature verification)
    const decodedToken = jwt.decode(token, { complete: true }) as {
      header: JwtHeader;
      payload: JwtPayload;
    };
    if (!decodedToken) {
      throw new Error('Failed to parse token');
    }

    const claims = decodedToken.payload;
    const iss = claims.iss as string;
    const parsedURL = new URL(iss);
    const userPoolID = parsedURL.pathname.substring(1);

    const response = await axios.get(`${getBaseURL()}/jwk/key-service/${userPoolID}/.well-known/jwks.json`);
    if (response.status !== 200) {
      throw new Error(`Request failed with status: ${response.status}`);
    }

    const result: JWKDict = response.data;

    // Extracting kid value from JWT header
    const jwtHeader = decodedToken.header;
    const kid = jwtHeader.kid;
    if (!kid) {
      throw new Error('Failed to get kid from token header');
    }

    // Finding the first key that matches the kid value
    const foundKey = result.keys.find((key) => key.kid === kid);

    return foundKey !== undefined;
  } catch (error) {
    const errMsg = 'fail to verify token';
    if (axios.isAxiosError(error)) {
      throw new Error(`${errMsg} code: ${error.response?.status}`);
    } else if (error instanceof Error) {
      throw new Error(`${errMsg} message: ${error.message}`);
    } else {
      throw new Error(`${errMsg} code: ${error}`);
    }
  }
}

// export async function loginScenario() {
//   const email: string = 'email@email.com'; // User email
//   const password: string = 'password'; // User password
//   const clientID: string = 'client id'; // Client ID
//   const clientSecret: string = 'client secret'; // Client Secret
//
//   // Creating Secure Channel
//   const secureChannelRes = await createSecureChannel();
//
//   // The password needs to be encrypted with the Secure Channel.
//   const encryptedPassword = encrypt(secureChannelRes, password);
//
//   // Client ID / Client Secret
//   const auth = Buffer.from(`${clientID}:${clientSecret}`).toString('base64'); //
//
//   // Login
//   const loginResult = await emailLogin(
//     email,
//     encryptedPassword,
//     secureChannelRes.ChannelID,
//     auth
//   );
//
//   // JWT token is generated upon success
//   console.log(`access token : ${loginResult.accessToken}`);
//   console.log(`refresh token : ${loginResult.refreshToken}`);
//
//   // Verifying JWT token
//   let verifyResult = await verifyToken(loginResult.accessToken);
//   console.log(`verify result : ${verifyResult}`);
//
//   // Reissuing token using refresh token
//   const refreshTokenResult = await refreshToken(loginResult.refreshToken, auth);
//
//   // JWT token is reissued upon success
//   console.log(`access token : ${loginResult.accessToken}`);
//   console.log(`refresh token : ${loginResult.refreshToken}`);
//
//   // Verifying JWT token
//   verifyResult = await verifyToken(refreshTokenResult.accessToken);
//   console.log(`verify result : ${verifyResult}`);
// }

export const v2Refresh = async (): Promise<string> => {
  try {
    const refreshToken = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}').refreshToken;

    const res = await axios.request({
      url: `${API_URL}/refresh`,
      method: 'post',
      // adapter: fetchAdapter,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        clientid: clientID,
        clientsecret: clientSecret,
        refreshtoken: refreshToken,
      }),
    });

    const abcAuth: AbcLoginResult = JSON.parse(res.data.abcAuth);
    if (abcAuth.accessToken) {
      window.sessionStorage.setItem('abcAuth', JSON.stringify(abcAuth));
      return abcAuth.accessToken;
    } else {
      console.log('abcAuth is empty');
      return '';
    }
  } catch (error: any) {
    console.log(error);
    return '';
  }
};

export const abcJoinV2 = async (email: string, code: string, socialtype: string, license: any): Promise<boolean> => {
  try {
    const res = await axios.request({
      url: `${API_URL}/join`,
      method: 'post',
      // adapter: fetchAdapter,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: queryString.stringify({
        clientid: clientID,
        clientsecret: clientSecret,
        email,
        code,
        socialtype,
        overage: license.overage,
        agree: license.agree,
        collect: license.collect,
        thirdparty: license.thirdparty,
        advertise: license.advertise,
      }),
    });
    console.log('!! abcJoinV2 result = ', res?.data);
    console.log('!! abcJoinV2 result message = ', res?.data?.message);
    console.log('!! abcJoinV2 result data = ', res?.data?.data);
    return res.status === 200 && res?.data?.data?.code === 1;
  } catch (error: any) {
    console.log(error);
    return false;
  }
};
